import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../Toast';

const editSchema = z.object({
  phone: z.string().min(10, 'Telefone deve ter pelo menos 10 dígitos'),
  email: z.string().email('E-mail inválido'),
});

type EditFormData = z.infer<typeof editSchema>;

interface EditPromoterSheetProps {
  promoter: {
    promoter_id: string;
    name: string;
    phone: string;
    email: string;
  };
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditPromoterSheet({ promoter, isOpen, onClose, onSaved }: EditPromoterSheetProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      phone: promoter.phone || '',
      email: promoter.email || '',
    },
  });

  // Reset form values when promoter changes or sheet opens
  useEffect(() => {
    if (isOpen) {
      reset({
        phone: promoter.phone || '',
        email: promoter.email || '',
      });
    }
  }, [isOpen, promoter, reset]);

  const mutation = useMutation({
    mutationFn: async (data: EditFormData) => {
      const res = await api.patch(`/supervisor/promoters/${promoter.promoter_id}`, data);
      return res.data;
    },
    onSuccess: () => {
      toast({ title: 'Dados atualizados com sucesso', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['supervisor', 'team-overview'] });
      onSaved();
      onClose();
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar dados', variant: 'error' });
    },
  });

  const onSubmit = (data: EditFormData) => {
    mutation.mutate(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl pb-safe animate-slide-up">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Editar promotor</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 active:bg-gray-100"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          <div className="text-center mb-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-lg mx-auto mb-1">
              {promoter.name.charAt(0)}
            </div>
            <p className="text-sm font-medium text-gray-900">{promoter.name}</p>
          </div>

          <div>
            <label htmlFor="phone" className="block text-xs font-medium text-gray-600 mb-1">
              Telefone
            </label>
            <input
              id="phone"
              type="tel"
              {...register('phone')}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
                errors.phone ? 'border-red-300' : 'border-gray-200'
              }`}
              placeholder="(11) 99999-9999"
            />
            {errors.phone && (
              <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              {...register('email')}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
                errors.email ? 'border-red-300' : 'border-gray-200'
              }`}
              placeholder="email@exemplo.com"
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={mutation.isPending}
            className="w-full py-3 rounded-xl bg-primary text-white font-medium text-sm active:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {mutation.isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar alterações'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
